<template>
  <div class="action-note">
    <div class="action-note__title">
      {{ date | formatDate }} | {{ userFullName }}
    </div>
    <div class="action-note__body">
      <div v-if="label" class="action-note__label">
        {{ label }}{{ assignedTo ? `: ${assignedTo}` : ':' }}
      </div>
      <!-- Render table for Administration Updates -->
      <div v-if="isAdminUpdate && parsedAdminUpdate.fields.length > 0">
        <!-- Show any non-pattern text first -->
        <div v-if="parsedAdminUpdate.preamble" class="admin-update-preamble">
          {{ parsedAdminUpdate.preamble }}
        </div>
        <!-- Then show the structured table -->
        <div class="admin-update-table">
          <table class="table table-striped table-bordered">
            <thead class="table-primary">
              <tr>
                <th>Field</th>
                <th>Previous</th>
                <th>New</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(field, index) in parsedAdminUpdate.fields"
                :key="index"
              >
                <td class="fw-bold">{{ field.name }}</td>
                <td>{{ field.previous || '—' }}</td>
                <td :class="{ 'text-muted': !field.new }">
                  {{ field.new || '(No new value provided)' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <!-- Fall back to regular rendering for non-admin updates -->
      <div v-else>{{ note }}</div>
    </div>
    <div class="action-note__footer">
      <div
        v-for="file in files"
        :key="file.url"
        class="action-note__file-container"
      >
        <div
          :class="'action-note__file ' + file.extension"
          v-viewer="{
            button: false,
            navbar: false,
            title: false,
            toolbar: false,
            url: 'full-image',
          }"
        >
          <img
            v-if="isImage(file)"
            :src="getFileThumbnail(file)"
            :full-image="getFileUrl(file)"
            alt=""
          />
          <a v-else :href="getFileUrl(file)" target="_blank">
            <font-awesome-icon class="icon" :icon="getFileIcon(file)">
            </font-awesome-icon>
          </a>
        </div>
        <div class="action-note__file-info">
          <div class="action-note__file-meta">
            <span class="action-note__file-name">{{ file.filename }}</span>
            <span v-if="file.dateTaken" class="action-note__file-date">
              ({{ file.dateTaken }})</span
            >
          </div>
          <a
            v-if="(canViewInVault || canUploadToVault) && isSavedToVault(file)"
            :href="getVaultUrl(file)"
            target="_blank"
            class="action-note__vault-link"
            >View in Document Vault</a
          >
          <button
            v-else-if="canUploadToVault"
            class="action-note__vault-btn"
            :disabled="!!savingFile[file.clientActionFileId]"
            @click="saveToVault(file)"
          >
            Save to Document Vault
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import riskhub from '@/services/Riskhub/Riskhub';
import api from '@/api';
import { isActionFilesDocumentVaultEnabled } from '@/config/features';

const DOCUMENT_TYPE_UUID = 'c93ebfc1-9d16-4158-b510-d6ef0f06541a';

export default {
  name: 'Action-Note',
  props: {
    date: {},
    user: {},
    adminUser: {},
    label: {
      type: String,
    },
    assignedTo: {
      type: String,
    },
    note: {
      type: String,
    },
    files: {
      default: () => [],
    },
    propertyId: {
      type: Number,
      required: true,
    },
    actionId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      savingFile: {},
    };
  },
  computed: {
    canUploadToVault() {
      return (
        isActionFilesDocumentVaultEnabled(this.$store.getters.authUser) &&
        this.$can(
          this.$perm.PROPERTIES_UPLOAD_DOCUMENTS,
          this.$perm.PERMISSION_TYPE_PROPERTIES,
        )
      );
    },
    canViewInVault() {
      return (
        isActionFilesDocumentVaultEnabled(this.$store.getters.authUser) &&
        this.$can(
          this.$perm.PROPERTIES_VIEW_PROPERTIES,
          this.$perm.PERMISSION_TYPE_PROPERTIES,
        )
      );
    },
    userFullName() {
      const user = this.user ? this.user : this.adminUser;
      if (user) {
        return `${user.firstName} ${user.lastName}`;
      }
      return '';
    },
    isAdminUpdate() {
      // Check if this is an Administration Update based on label
      return (
        this.label && this.label.toLowerCase().includes('administration update')
      );
    },
    parsedAdminUpdate() {
      if (!this.isAdminUpdate || !this.note) {
        return { preamble: '', fields: [] };
      }

      const content = this.note;

      // Remove the "Administration update:" prefix if present
      const cleanContent = content.replace(/^Administration update:\s*/i, '');

      // Find the first occurrence of "Previous [Field]" to split preamble from structured content
      const firstPatternMatch = cleanContent.match(/\bPrevious\s+[A-Z][a-z]+\s+/);

      let preamble = '';
      let structuredContent = cleanContent;

      if (firstPatternMatch) {
        const splitIndex = firstPatternMatch.index;
        preamble = cleanContent.substring(0, splitIndex).trim();
        structuredContent = cleanContent.substring(splitIndex);
      }

      // Known field names that can appear in administration updates
      const KNOWN_FIELD_NAMES = [
        'FRA Action Due Date',
        'FRA Review Status',
        'Assessment Risk Rating',
        'Recommendation',
        'Status',
        'Priority',
        'Deleted',
      ];

      /**
       * Normalize whitespace in text (collapse multiple spaces to single space).
       */
      const normalizeWhitespace = text => text.replace(/\s+/g, ' ');

      /**
       * Find which known field name matches the start of the text.
       * Returns the field name if found, null otherwise.
       */
      const findFieldNameInText = (normalizedText) => {
        for (const fieldName of KNOWN_FIELD_NAMES) {
          if (normalizedText.startsWith(fieldName)) {
            return fieldName;
          }
        }
        return null;
      };

      /**
       * Extract the value by removing the field name prefix and cleaning up.
       */
      const extractValue = (normalizedText, fieldName) => {
        const value = normalizedText.substring(fieldName.length).trim();
        // Remove leading/trailing periods and whitespace
        return value.replace(/^[.\s]+|[.\s]+$/g, '');
      };

      // Match all "Previous" or "New" entries with their content
      const entryPattern = /(Previous|New)\s+(.+?)(?=\s+(?:Previous|New)\s+|$)/gs;
      const entries = [...structuredContent.matchAll(entryPattern)];

      // Build field map by pairing Previous/New entries
      const fieldMap = new Map();

      for (let i = 0; i < entries.length; i++) {
        const [, type, content] = entries[i];

        // Only process "Previous" entries (they should be followed by "New")
        if (type !== 'Previous') continue;

        const nextEntry = entries[i + 1];
        if (!nextEntry || nextEntry[1] !== 'New') continue;

        // Normalize whitespace to handle multiple spaces
        const previousContent = normalizeWhitespace(content.trim());
        const newContent = normalizeWhitespace(nextEntry[2].trim());

        // Find the field name from the known list
        const fieldName = findFieldNameInText(previousContent);

        if (fieldName) {
          const previousValue = extractValue(previousContent, fieldName);
          const newValue = extractValue(newContent, fieldName);

          fieldMap.set(fieldName, {
            name: fieldName,
            previous: previousValue,
            new: newValue,
          });

          i++; // Skip next entry since we processed it as a pair
        }
      }

      return {
        preamble,
        fields: Array.from(fieldMap.values()),
      };
    },
  },
  filters: {
    formatDate(date) {
      return riskhub.formatDateTime(date);
    },
  },
  methods: {
    isSavedToVault(file) {
      return !!file.documentVaultFile && !file.documentVaultFile.isHidden;
    },
    getVaultUrl(file) {
      if (!file.documentVaultFile) return null;
      const baseUrl = process.env.VUE_APP_MONOREPO_URL;
      if (!baseUrl) return null;
      // Open the document within its property (the doc is linked to the property
      // on save). The document-detail route/endpoint key on the document UUID;
      // the property segment accepts the numeric propertyId or a UUID.
      return `${baseUrl.replace(/\/$/, '')}/properties/${this.propertyId}/documents/${file.documentVaultFile.uuid}`;
    },
    async saveToVault(file) {
      this.$set(this.savingFile, file.clientActionFileId, true);
      try {
        await api.post('v1/document-vault/documents', {
          client_id: this.$store.getters.userClientId,
          documents: [
            {
              file: { upload_type: 'url', url: file.url },
              name: file.filename,
              document_type_uuid: DOCUMENT_TYPE_UUID,
              entities: [
                {
                  entity_type: 'property',
                  entity_id: this.propertyId,
                },
                {
                  entity_type: 'action',
                  entity_id: this.actionId,
                },
                {
                  entity_type: 'action-file',
                  entity_id: file.clientActionFileId,
                },
              ],
              no_issue_date: 1,
              no_expiry_date: 1,
            },
          ],
        });
        this.$notify({
          type: 'success',
          title: 'Success',
          text: 'File saved to Document Vault.',
        });
        this.$emit('saved-to-vault');
      } catch (e) {
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Failed to save file to Document Vault.',
        });
      } finally {
        this.$delete(this.savingFile, file.clientActionFileId);
      }
    },
    getFileThumbnail(file) {
      return file.thumbnailUrl;
    },
    /**
     * Get the URL for the file
     */
    getFileUrl(file) {
      return file.url;
    },
    urlEncodeFileName(filePath) {
      const urlPathParts = filePath.split('/');
      const fileNamePathIndex = urlPathParts.length - 1;
      urlPathParts[fileNamePathIndex] = encodeURIComponent(urlPathParts[fileNamePathIndex]);
      return urlPathParts.join('/');
    },
    /**
     * Check if the file is a PDF
     * @param extension
     * @returns {boolean}
     */
    isImage({ extension }) {
      return ['jpg', 'jpeg', 'png'].includes(extension);
    },
    getFileIcon({ extension }) {
      let icon;
      switch (extension) {
        case 'doc':
        case 'docx':
          icon = 'file-word';
          break;

        case 'xls':
        case 'xlsx':
        case 'csv':
          icon = 'file-excel';
          break;

        case 'pdf':
          icon = 'file-pdf';
          break;

        case 'ppt':
        case 'pptx':
        case 'pptm':
        case 'pps':
        case 'ppsx':
          icon = 'file-powerpoint';
          break;

        case 'eml':
          icon = 'envelope';
          break;

        default:
          icon = 'file';
          break;
      }

      return ['fas', icon];
    },
  },
};
</script>

<style lang="scss">
.action-note {
  border: 1px solid $border-color;
  border-radius: 3px;
  padding: 20px 22px;
  margin-bottom: 25px;
  font-size: $font-size-sm;
  color: $gray-900;

  a {
    color: $gray-900;
  }

  &__title {
    margin-bottom: 5px;
    font-weight: 600;
  }

  &__label {
    font-weight: 600;
    margin-bottom: 5px;
  }

  &__body {
    margin-bottom: 15px;

    .admin-update-preamble {
      margin-bottom: 10px;
      font-style: italic;
      color: $gray-600;
    }

    .admin-update-table {
      margin-top: 10px;

      .table {
        font-size: $font-size-sm;
        margin-bottom: 0;

        th {
          font-weight: 600;
          font-size: $font-size-sm;
        }

        td {
          vertical-align: top;
          word-wrap: break-word;

          &.fw-bold {
            font-weight: 600;
            white-space: nowrap;
          }
        }
      }
    }
  }

  &__footer {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__file-container {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 12px;
  }

  &__file-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    justify-content: flex-start;
  }

  &__file-meta {
    line-height: 1.4;
  }

  &__file-name {
    font-weight: 600;
  }

  &__file-date {
    color: $gray-600;
  }

  &__vault-btn {
    background: none;
    border: none;
    padding: 0;
    text-align: left;
    font-size: $font-size-sm;
    color: $gray-900;
    text-decoration: underline;
    cursor: pointer;

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }

  &__vault-link,
  &__vault-saved {
    font-size: $font-size-sm;
  }

  &__file {
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    border-radius: 2px;
    overflow: hidden;

    &.pdf {
      background: $red;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &.doc,
    &.docx {
      background: #285192;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &.xls,
    &.xlsx,
    &.csv {
      background: #206f44;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &.ppt,
    &.pptx,
    &.pptm,
    &.pps,
    &.ppsx {
      background: #d24726;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &.eml {
      background: #0072c6;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .icon {
      font-size: 32px;
      color: $white;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 2px;
      cursor: pointer;
    }
  }
}

.fancy-box-mask {
  z-index: 1100;
}
</style>
